import type { LibraryItem } from "@/lib/mock/library";

const VIDEO_ACCENT = "#f43f5e";

/**
 * The single "start here" onboarding walkthrough. This is the video that
 * auto-plays in a modal for a brand-new user right after the survey, and it
 * is highlighted as the first card in the Tutorials tab. Change ONLY this id
 * to swap which catalog video is the onboarding video.
 */
export const ONBOARDING_VIDEO_ID = "video-user-success-333";

// NOTE: the old "What is Xynth | Data sources, automations, research" video
// (R_Al-s8OdO4) was removed 2026-07-17 — content is outdated (pre Skills /
// Signals). Re-add here when a refreshed platform walkthrough is recorded.

/** Real YouTube walkthroughs for the Library → Tutorials tab. */
export const LIBRARY_VIDEOS: LibraryItem[] = [
  {
    id: ONBOARDING_VIDEO_ID,
    kind: "video",
    title: "How This Trader Used AI to Make $333 | Xynth User Success Story",
    blurb: "",
    meta: "Xynth Finance",
    accent: VIDEO_ACCENT,
    topic: "",
    featured: true,
    youtubeUrl: "https://www.youtube.com/watch?v=ZyQ-LCVCh64",
    thumbnailUrl: "https://i.ytimg.com/vi/ZyQ-LCVCh64/maxresdefault.jpg",
    muxPlaybackId: "vqsHqioinznHEdsMYvbHgj377edF2e3yAmrGkk1PaFQ",
  },
  {
    id: "video-10k-to-60k",
    kind: "video",
    title: "How I Turned $10k into $60k in 7 Months trading with AI",
    blurb: "",
    meta: "Xynth Finance",
    accent: VIDEO_ACCENT,
    topic: "",
    youtubeUrl: "https://www.youtube.com/watch?v=VyfevlF1oNw",
    thumbnailUrl: "https://i.ytimg.com/vi/VyfevlF1oNw/maxresdefault.jpg",
  },
  {
    id: "video-more-soon",
    kind: "video",
    title: "More coming soon",
    blurb: "",
    meta: "",
    accent: VIDEO_ACCENT,
    topic: "",
    comingSoon: true,
  },
];

/** The onboarding walkthrough item (or null if the id ever goes missing). */
export function getOnboardingVideo(): LibraryItem | null {
  return LIBRARY_VIDEOS.find((v) => v.id === ONBOARDING_VIDEO_ID) ?? null;
}

// ── "Has this user seen the onboarding video?" flag (localStorage) ──────────
// Fast-path CACHE only — the DB (users.alerts_onboarding.completed via
// /onboarding/alerts-status) is the source of truth. Versioned key (_v2):
// v1 was written by the short-lived localStorage-only build on 2026-07-16
// and must not block the DB-gated rollout.
const LS_VIDEO_SEEN_PREFIX = "xynth.video_onboarding_seen_v2:";

export function hasSeenOnboardingVideo(uid: string | null | undefined): boolean {
  if (!uid || typeof window === "undefined") return true;
  try {
    return (
      window.localStorage.getItem(`${LS_VIDEO_SEEN_PREFIX}${uid}`) === "true"
    );
  } catch {
    // Private mode / quota — fail closed so we never repeat-pop on every load.
    return true;
  }
}

export function markOnboardingVideoSeen(uid: string | null | undefined): void {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${LS_VIDEO_SEEN_PREFIX}${uid}`, "true");
  } catch {
    /* private mode / quota — ignore */
  }
}

export function youtubeVideoId(url: string): string | null {
  const match =
    url.match(/[?&]v=([^&]+)/) ?? url.match(/youtu\.be\/([^?&]+)/);
  return match?.[1] ?? null;
}

/** Embed URL for the in-app player modal. Prefers Mux over YouTube. */
export function youtubeEmbedSrc(item: LibraryItem): string | null {
  // Prefer Mux if available (no bot detection issues)
  if (item.muxPlaybackId) {
    return `https://stream.mux.com/${item.muxPlaybackId}.m3u8`;
  }
  
  // Fallback to YouTube embed
  if (!item.youtubeUrl) return null;
  const id = youtubeVideoId(item.youtubeUrl);
  if (!id) return null;
  const start = item.youtubeStartSeconds ?? 0;
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    autoplay: "1",
  });
  if (start > 0) params.set("start", String(start));
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}
